import { handleActions } from 'redux-actions';
import { defaultGdprWidgetState } from './defaults';
import { GdprWidgetData, GdprWidgetStep } from './types';
import {
  initPersonalDataDownloadPage,
  initPersonalDataPage,
} from '../../actions';
import {
  setGdprWidgetStep,
  submitPersonalDataRemoveRequest,
  submitPersonalDataRemoveRequestFailure,
  submitPersonalDataRemoveRequestSuccess,
  submitPersonalDataRequest,
  submitPersonalDataRequestFailure,
  submitPersonalDataRequestSuccess,
  submitPersonalDataResendDataRemovalConfirmationLink,
  submitPersonalDataResendDataRemovalConfirmationLinkFailure,
  submitPersonalDataResendDataRemovalConfirmationLinkSuccess,
  submitPersonalDataResendDownloadLink,
  submitPersonalDataResendDownloadLinkSuccess,
  submitPersonalDataResendDownloadLinkFailure,
  submitPersonalDataConfirmRemoval,
  submitPersonalDataConfirmRemovalSuccess,
  submitPersonalDataConfirmRemovalFailure,
} from './actions';
import { NetworkRequestState } from '../baseTypes';
import { networkRequestMetaReducer } from '../utils';

function determineStepFromInit(
  action: ReturnType<typeof initPersonalDataPage>
) {
  const { emailQueryParams, fromDeactivation, isDeactivated } =
    action.payload.data;
  if (fromDeactivation && isDeactivated) {
    return GdprWidgetStep.RemoveDataDeactivated;
  } else if (emailQueryParams?.ts && emailQueryParams?.token) {
    return GdprWidgetStep.RemoveDataInProgress;
  }
  return GdprWidgetStep.Initial;
}

export default handleActions<GdprWidgetData, any>(
  {
    [initPersonalDataPage.toString()]: (
      state,
      action: ReturnType<typeof initPersonalDataPage>
    ) => {
      const {
        isDeactivated,
        isGdprLinkSent,
        isRemoveRequestInProgress,
        isPersonaldataRequestInitiated,
        isRemoveRequestInitiated,
        emailQueryParams,
      } = action.payload.data;
      return {
        ...defaultGdprWidgetState,
        step: determineStepFromInit(action),
        isPersonaldataRequestInitiated: isPersonaldataRequestInitiated,
        isRemoveRequestInitiated: isRemoveRequestInitiated,
        isRemoveRequestInProgress: isRemoveRequestInProgress,
        isGdprLinkSent: isGdprLinkSent,
        isDeactivated: isDeactivated,
        emailQueryParams: emailQueryParams,
        networkRequests: {
          ...defaultGdprWidgetState.networkRequests,
          initiateRemoveDataRequest: {
            status: isRemoveRequestInitiated
              ? NetworkRequestState.SUCCESS
              : NetworkRequestState.IDLE,
            errors: {},
          },
        },
      };
    },
    [initPersonalDataDownloadPage.toString()]: (
      state,
      action: ReturnType<typeof initPersonalDataDownloadPage>
    ) => {
      return {
        ...state,
        step: GdprWidgetStep.DownloadData,
        personalDataDownloadLink: action.payload.data.link,
      };
    },
    [setGdprWidgetStep.toString()]: (
      state,
      action: ReturnType<typeof setGdprWidgetStep>
    ) => ({
      ...state,
      step: action.payload.step,
    }),
    ...networkRequestMetaReducer({
      initialAction: submitPersonalDataRequest,
      successAction: submitPersonalDataRequestSuccess,
      failureAction: submitPersonalDataRequestFailure,
      requestDataPlacer: (state: GdprWidgetData, requestMeta) => ({
        networkRequests: {
          ...state.networkRequests,
          personalDataRequest: {
            ...requestMeta,
          },
        },
      }),
      additionalStateUpdates: {
        successStateUpdate: (state: GdprWidgetData, action) => ({
          isPersonaldataRequestInitiated: true,
        }),
      },
    }),
    ...networkRequestMetaReducer({
      initialAction: submitPersonalDataRemoveRequest,
      successAction: submitPersonalDataRemoveRequestSuccess,
      failureAction: submitPersonalDataRemoveRequestFailure,
      requestDataPlacer: (state: GdprWidgetData, requestMeta) => ({
        networkRequests: {
          ...state.networkRequests,
          initiateRemoveDataRequest: {
            ...requestMeta,
          },
        },
      }),
      additionalStateUpdates: {
        initialStateUpdate: (state: GdprWidgetData, action) => ({
          isRemoveRequestInitiated: true,
        }),
        successStateUpdate: (state: GdprWidgetData, action) => ({
          isRemoveRequestInitiated: true,
          isRemoveRequestInProgress: false,
        }),
        failureStateUpdate: (state: GdprWidgetData, action) => ({
          isRemoveRequestInitiated: false,
          isRemoveRequestInProgress: false,
        }),
      },
    }),
    ...networkRequestMetaReducer({
      initialAction: submitPersonalDataResendDataRemovalConfirmationLink,
      successAction: submitPersonalDataResendDataRemovalConfirmationLinkSuccess,
      failureAction: submitPersonalDataResendDataRemovalConfirmationLinkFailure,
      requestDataPlacer: (state: GdprWidgetData, requestMeta) => ({
        networkRequests: {
          ...state.networkRequests,
          resendRemoveConfirmationRequest: {
            ...requestMeta,
          },
        },
      }),
    }),
    ...networkRequestMetaReducer({
      initialAction: submitPersonalDataResendDownloadLink,
      successAction: submitPersonalDataResendDownloadLinkSuccess,
      failureAction: submitPersonalDataResendDownloadLinkFailure,
      additionalStateUpdates: {
        successStateUpdate: (state: GdprWidgetData, action) => ({
          isGdprLinkSent: true,
        }),
      },
      requestDataPlacer: (state: GdprWidgetData, requestMeta) => ({
        networkRequests: {
          ...state.networkRequests,
          resendDownloadLinkRequest: {
            ...requestMeta,
          },
        },
      }),
    }),
    ...networkRequestMetaReducer({
      initialAction: submitPersonalDataConfirmRemoval,
      successAction: submitPersonalDataConfirmRemovalSuccess,
      failureAction: submitPersonalDataConfirmRemovalFailure,
      requestDataPlacer: (state: GdprWidgetData, requestMeta) => ({
        networkRequests: {
          ...state.networkRequests,
          confirmRemoveDataRequest: {
            ...requestMeta,
          },
        },
      }),
    }),
  },
  defaultGdprWidgetState
);
